import { Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useBackState } from "@/book/components/back";
import { ExperienceCard } from "@/book/components/experience-list";
import { ExperienceForm } from "@/book/components/experience-form";
import {
  useChapters,
  useExercise,
  useExperiences,
  type Experience,
  type ExperienceQuery,
} from "@/shared/api/book";
import { chapterOptions } from "@/shared/lib/labels";
import { num, useFilters } from "@/shared/lib/use-filters";
import { Button } from "@/shared/ui/button";
import { FilterRow, FilterSelect, SearchInput } from "@/shared/ui/filters";
import { PageTitle } from "@/shared/ui/page-header";
import { Sheet } from "@/shared/ui/sheet";
import { Empty, ErrorNote, ListSkeleton, Pager } from "@/shared/ui/state";

const DEFAULTS = { q: "", chapter_id: "", exercise_id: "", sort: "new", who: "", page: "1" };

const SORTS = [
  { value: "new", label: "Legújabb" },
  { value: "liked", label: "Legkedveltebb" },
];

export function ExperiencesPage() {
  const { values, set } = useFilters(DEFAULTS);
  const { data: chapters } = useChapters();
  const [open, setOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<Experience | null>(null);

  const query: ExperienceQuery = {
    q: values.q,
    chapter_id: num(values.chapter_id),
    exercise_id: num(values.exercise_id),
    sort: values.sort as "new" | "liked",
    who: (values.who || undefined) as ExperienceQuery["who"],
    page: Number(values.page) || 1,
  };
  const { data, isPending, isError } = useExperiences(query);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <PageTitle>Tapasztalatok</PageTitle>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
          <Plus size={16} />
          Megosztok én is
        </Button>
      </div>

      <SearchInput
        label="Keresés a tapasztalatok között"
        placeholder="Keresés a tapasztalatok között"
        value={values.q}
        onChange={(q) => set({ q })}
      />

      <FilterRow>
        <FilterSelect
          label="Rendezés"
          options={SORTS}
          value={values.sort}
          onChange={(sort) => set({ sort })}
        />
        <FilterSelect
          label="Mit mutassunk"
          allLabel="Mindenkié"
          options={[
            { value: "mine", label: "Csak a sajátjaim" },
            { value: "liked", label: "Kedvenceim" },
          ]}
          value={values.who}
          onChange={(who) => set({ who })}
        />
        {chapters && chapters.length > 0 && (
          <FilterSelect
            label="Fejezet"
            allLabel="Minden fejezet"
            options={chapterOptions(chapters)}
            value={values.chapter_id}
            onChange={(chapter_id) => set({ chapter_id })}
          />
        )}
      </FilterRow>

      {values.exercise_id && (
        <ExerciseFilterNote
          exerciseId={Number(values.exercise_id)}
          onClear={() => set({ exercise_id: "" })}
        />
      )}

      {isPending && <ListSkeleton />}
      {isError && <ErrorNote />}
      {data && (
        <>
          <p className="text-[12px] text-muted">{data.total} tapasztalat</p>
          {data.items.length === 0 ? (
            <Empty
              action={
                <Button variant="outline" onClick={() => setOpen(true)}>
                  Megosztom az elsőt
                </Button>
              }
            >
              Erre még nincs megosztott tapasztalat.
            </Empty>
          ) : (
            <div className="flex flex-col gap-4">
              {data.items.map((x) => (
                <ExperienceCard key={x.id} experience={x} onReply={() => setReplyTo(x)} />
              ))}
            </div>
          )}
          <Pager
            page={data.page}
            total={data.total}
            pageSize={data.page_size}
            onChange={(page) => set({ page: String(page) })}
          />
        </>
      )}

      <Sheet
        open={open}
        onOpenChange={setOpen}
        title="Megosztom a tapasztalatom"
        description="Jóváhagyás után jelenik meg — addig csak te látod."
      >
        <ExperienceForm
          chapterId={num(values.chapter_id)}
          exerciseId={num(values.exercise_id)}
          onDone={() => setOpen(false)}
        />
      </Sheet>

      <Sheet
        open={replyTo !== null}
        onOpenChange={(v) => !v && setReplyTo(null)}
        title="Nekem is van ezzel tapasztalatom"
        description={replyTo ? `Válasz erre: ${replyTo.author}` : undefined}
      >
        {replyTo && <ExperienceForm parentId={replyTo.id} onDone={() => setReplyTo(null)} />}
      </Sheet>
    </div>
  );
}


/** A gyakorlat-szűrés jelzése névvel — így látszik, melyik gyakorlatról van szó. */
function ExerciseFilterNote({
  exerciseId,
  onClear,
}: {
  exerciseId: number;
  onClear: () => void;
}) {
  const { data: exercise } = useExercise(exerciseId);
  const back = useBackState();
  return (
    <p className="text-[13px] text-muted">
      Ehhez a gyakorlathoz tartozó tapasztalatok:{" "}
      <Link
        to={`/app/book/gyakorlatok/${exerciseId}`}
        state={back}
        className="font-semibold text-gold-text"
      >
        {exercise?.title ?? "…"}
      </Link>
      .{" "}
      <button type="button" className="font-semibold text-gold-text" onClick={onClear}>
        Mind mutatása
      </button>
    </p>
  );
}
